import { FC } from "react";
import {
  Pagination,
  Stack,
  TableContainer as MUITableContainer,
  Table as MUITable,
  TableHead as MUITableHead,
  TableRow as MUITableRow,
  TableCell as MUITableCell,
  TableBody as MUITableBody,
} from "@mui/material";
import React from "react";
import { PaginatedCTsRes } from "src/store/features/api/generated";
import {
  dynamicPriceFormatter,
  dynamicUnitFormatter,
} from "src/utils/helpers/PriceAndUnitsHelpers";
import {
  createDataDesktop,
  createLinesDesktop,
  getDate,
  getTime,
  iconSelector,
  statusSelector,
} from "./helperfns";
import Row from "./Row";

/** A container component for a table element.*/
const TableContainer: FC<{ children: React.ReactNode }> = ({ children }) => (
  <MUITableContainer>{children}</MUITableContainer>
);

/** A styled wrapper around Material-UI's Table component.*/
const Table: FC<{ children: React.ReactNode }> = ({ children }) => (
  <MUITable aria-label="collapsible table" sx={{ tableLayout: "fixed", width: "100%" }}>
    {children}
  </MUITable>
);

/** A component that renders a table head element for a Material UI table. */
const TableHead: FC<{ children: React.ReactNode }> = ({ children }) => (
  <MUITableHead>{children}</MUITableHead>
);

/** A styled Material-UI table row component with a bottom border. */
const TableRow: FC<{ children: React.ReactNode }> = ({ children }) => (
  <MUITableRow sx={{ borderBottom: "2px solid #000" }}>{children}</MUITableRow>
);

/**
 * Customized table cell component that wraps the `MUITableCell` component. Allows for
 * specifying the font weight and width of the cell as props.
 */
const TableCell: FC<{ fontWeight?: string; width?: string; children: React.ReactNode }> = ({
  fontWeight,
  width,
  children,
}) => <MUITableCell sx={{ fontWeight, width }}>{children}</MUITableCell>;

/** A wrapper component for the `TableBody` component of the Material-UI `Table` component. */
const TableBody: FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => (
  <MUITableBody id={id}>{children}</MUITableBody>
);

interface TransactionTableProps {
  transactions: PaginatedCTsRes | undefined;
  onPageChange: (event: React.ChangeEvent<unknown>, newPage: number) => void;
}

const TransactionTable = (props: TransactionTableProps) => {
  const { transactions, onPageChange } = props;
  const pageCount = Math.ceil((transactions ? transactions.totalCTs : 1) / 10);
  const currentPage = transactions?.currentPage ?? 1;

  /**
   * Creates an array of data rows to be displayed in a table. Each row is an object with properties such as
   * the transaction ID, transaction icon, type, date, time, currency, status, lines, and notes. This function
   * uses the React useMemo hook to memoize the data and optimize performance. If the transactions data is not
   * available, an empty array is returned.
   *
   * @param transactions An array of currency transaction objects containing data such as the transaction ID, type,
   *                     created date, quote currency, status, note, and lines.
   * @returns An array of data rows to be displayed in a table.
   */
  const rows = React.useMemo((): ReturnType<typeof createDataDesktop>[] | [] => {
    if (!transactions) return [];

    const rows = [] as ReturnType<typeof createDataDesktop>[];

    transactions.data.forEach((row) => {
      const id = row.id;
      const icon = iconSelector(row.type);
      const type = row.type;
      // const date = getDate(row.completed ?? row.created);
      const date = getDate(row.completed ?? row.created);
      const time = getTime(row.completed ?? row.created);
      const currency = row.lines.map((line) => {
        const code = line.currencyConfig.code;
        return {
          code: code,
          img: line.currencyConfig.logoUrl ?? code,
        };
      });

      const status = statusSelector(row.status);
      const note = row.note;
      const amount = dynamicPriceFormatter(row.value, row.quoteCurrencyConfig.code);

      const lines = row.lines.map((line) => {
        const side = line.side ?? "";
        const quote = line.quoteCurrencyConfig.code;
        const base = [
          line.currencyConfig.logoUrl ?? line.currencyConfig.code,
          line.currencyConfig.displayName ?? "",
        ];
        const units = dynamicUnitFormatter(line.units, line.price);
        const price = dynamicPriceFormatter(line.price, line.quoteCurrencyConfig.code);
        const value = dynamicPriceFormatter(line.value, line.quoteCurrencyConfig.code);
        const fee = dynamicPriceFormatter(line.fee, line.quoteCurrencyConfig.code);
        return createLinesDesktop(side, quote, base, units, price, value, fee);
      });

      rows.push(
        createDataDesktop(id, icon, type, date, time, currency, amount, status, lines, note),
      );
    });

    return rows;
  }, [transactions]);

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell fontWeight="bold" width="20%">
              Type
            </TableCell>
            <TableCell fontWeight="bold" width="33%">
              Currency
            </TableCell>
            <TableCell fontWeight="bold" width="14%">
              Date
            </TableCell>
            <TableCell fontWeight="bold" width="14%">
              Amount
            </TableCell>
            <TableCell fontWeight="bold" width="14%">
              Status
            </TableCell>
            <TableCell width="7%">{""}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody id="transactions-tablebody">
          {rows.map((row) => (
            <Row key={row.id} row={row} />
          ))}
        </TableBody>
      </Table>
      <Stack alignItems="center" justifyContent="center" style={{ padding: "30px" }}>
        <Pagination
          color="secondary"
          count={pageCount}
          page={currentPage}
          onChange={onPageChange}
        />
      </Stack>
    </TableContainer>
  );
};

export default TransactionTable;
